import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from "react";
import {
  LayoutDashboard, ShoppingCart, Package, Users, Wallet, Layers,
  Sun, Moon, Globe, LogOut, Plus, Search, Printer, Truck, DollarSign,
  X, CheckCircle2, Send, Trash2, Pencil, Filter, FileDown, ChevronRight, ChevronDown, ChevronLeft,
  AlertTriangle, TrendingUp, Boxes, Receipt, Settings, MessageCircle,
  Sparkles, Grid, Ruler, FileText, Factory, Upload, Bell, CreditCard, ClipboardList, RefreshCw,
  Home, ChevronsLeft, ChevronsRight, Mail, Share2, Eye, EyeOff, Check, CalendarDays, Camera, FileSignature, Target, MapPin, Tag, Palette, AlertCircle, Download,
  Brain, Zap, Cpu, Info, Building2, History, Phone, Shield, BarChart2, Bot, Radio, Copy, Barcode, PlusCircle, ShieldAlert, ExternalLink, Loader2, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { AuthCtx, UICtx, StockSettingsCtx } from "../appContexts";
import { fmt, fmtDate, fmtDT, ono, MONEY, convertPrice } from "../appUtils";
import { translations, CURRENCY_SYMBOL, STATUS_LABELS } from "../mock";
import { MarginReport, PurchaseListsModule } from "../purchaseLists";
import { ModuleIcon } from "../components/layout/Sidebar";

const REPORT_GROUP_META = {
  stor:              { label: "Stor Perde",              color: "#3B82F6" },
  stor_sistem:       { label: "Stor Sistemi (Kumaşsız)", color: "#60A5FA" },
  stor_control:      { label: "Stor Kumanda/Aksesuarı",  color: "#93C5FD" },
  stor_motor:        { label: "Stor Motor",              color: "#BFDBFE" },
  stor_sistem_motor: { label: "Stor Sistemi Motor",      color: "#DBEAFE" },
  zebra:             { label: "Zebra Perde",             color: "#8B5CF6" },
  plise14:           { label: "PLİCELL 14mm",            color: "#EC4899" },
  plise25:           { label: "Plise 20/26mm",           color: "#F472B6" },
  dikey:             { label: "Dikey Perde",             color: "#10B981" },
  dikey_ray:         { label: "Dikey Ray",               color: "#34D399" },
  dikey_motor:       { label: "Dikey Motor",             color: "#6EE7B7" },
  zip_art110_helio:  { label: "Zip Perde — Art Serisi",  color: "#F59E0B" },
  skyzip_1400:       { label: "Zip Perde — Sky Serisi",  color: "#FCD34D" },
  sineklik_dikey:    { label: "Sineklik Dikey/Yatay",    color: "#6B7280" },
  sineklik_duble_yatay:{ label: "Sineklik Duble Yatay",  color: "#9CA3AF" },
  surme_sineklik:    { label: "Sürme Sineklik",          color: "#D1D5DB" },
  metal_jaluzi_16_25:{ label: "Metal Jaluzi 16-25mm",    color: "#EF4444" },
  ahsap_jaluzi_25:   { label: "Ahşap Jaluzi 25mm",       color: "#92400E" },
  ahsap_jaluzi_35:   { label: "Ahşap Jaluzi 35mm",       color: "#B45309" },
  ahsap_jaluzi_50:   { label: "Ahşap Jaluzi 50mm",       color: "#D97706" },
  evax_50:           { label: "Evax 50mm Ahşap",         color: "#F59E0B" },
  silhouette:        { label: "Silhouette",              color: "#7C3AED" },
  ciftli_sistem:     { label: "Çiftli Sistem",           color: "#0EA5E9" },
  sadece_kumas:      { label: "Sadece Kumaş",            color: "#14B8A6" },
};

const MONTHS_TR = ["","Oca","Şub","Mar","Nis","May","Haz","Tem","Ağu","Eyl","Eki","Kas","Ara"];

function slugLabel(slug) {
  const known = REPORT_GROUP_META[slug]?.label;
  if (known) return known;
  // Veri kaynağındaki ham grup adları — kullanıcıya düzgün görünür (24.10.2026 tasarım turu)
  const SPECIAL = { diger: "Diğer", "diger grup": "Diğer Grup", genel: "Genel", standart: "Standart", digerleri: "Diğerleri" };
  const s = String(slug || "");
  if (SPECIAL[s.toLocaleLowerCase("tr")]) return SPECIAL[s.toLocaleLowerCase("tr")];
  return s.replace(/[_-]+/g, " ").replace(/\b\w/g, (c) => c.toLocaleUpperCase("tr")) || "—";
}
function slugColor(slug) {
  return REPORT_GROUP_META[slug]?.color || "#64748B";
}

function exportCSV(rows, filename) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  const csv = [keys.join(";"), ...rows.map(r => keys.map(k => String(r[k] ?? "").replace(/;/g, ",")).join(";"))].join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function ProductGroupReport({ data, maxTL }) {
  const [expanded, setExpanded] = useState({});
  if (!data) return null;
  return (
    <div className="space-y-2">
      {data.map(g => {
        const pct = maxTL > 0 ? Math.round(g.totalTL / maxTL * 100) : 0;
        const isOpen = expanded[g.groupSlug];
        return (
          <div key={g.groupSlug} className="zen-panel overflow-hidden">
            <button
              className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-50 dark:hover:bg-[#1a2540] transition-colors"
              onClick={() => setExpanded(e => ({ ...e, [g.groupSlug]: !isOpen }))}
              data-testid={`report-group-${g.groupSlug}`}
            >
              <span className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: slugColor(g.groupSlug) }} />
              <span className="font-semibold text-sm flex-1 text-left">{slugLabel(g.groupSlug)}</span>
              <div className="hidden md:flex items-center gap-6 text-xs text-slate-500">
                <span className="zen-mono w-16 text-right">{g.count} adet</span>
                {g.m2 > 0 && <span className="zen-mono w-16 text-right">{g.m2.toFixed(1)} m²</span>}
                <span className="zen-mono w-24 text-right font-bold text-slate-800 dark:text-slate-200">{g.totalTL.toLocaleString("tr-TR", {maximumFractionDigits:0})} ₺</span>
              </div>
              <div className="w-32 hidden md:block">
                <div className="h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                  <div className="h-2 rounded-full transition-all" style={{ width: `${pct}%`, background: slugColor(g.groupSlug) }} />
                </div>
                <div className="text-[10px] text-right text-slate-400 mt-0.5">%{pct}</div>
              </div>
              <ChevronRight className={`w-4 h-4 text-slate-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} />
            </button>
            {/* Mobil satır */}
            <div className="flex md:hidden items-center justify-between px-4 pb-3 gap-3">
              <span className="text-xs text-slate-500 zen-mono">{g.count} adet{g.m2 > 0 ? ` · ${g.m2.toFixed(1)} m²` : ""}</span>
              <span className="text-sm font-bold zen-mono">{g.totalTL.toLocaleString("tr-TR", {maximumFractionDigits:0})} ₺</span>
              <div className="flex-1 h-1.5 rounded-full bg-slate-100 dark:bg-slate-700">
                <div className="h-1.5 rounded-full" style={{ width: `${pct}%`, background: slugColor(g.groupSlug) }} />
              </div>
            </div>
            {isOpen && g.subItems?.length > 0 && (
              <div className="border-t border-slate-100 dark:border-[#22304A] bg-slate-50 dark:bg-[#111827] px-4 py-3 space-y-1.5">
                <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-2">Detay Döküm</div>
                {g.subItems.map((s, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-300 flex-shrink-0" />
                    <span className="flex-1 text-slate-700 dark:text-slate-300">{s.label}</span>
                    <span className="zen-mono text-slate-500 w-12 text-right">{s.count} adet</span>
                    {s.m2 > 0 && <span className="zen-mono text-slate-400 w-14 text-right">{s.m2.toFixed(1)} m²</span>}
                    <span className="zen-mono font-semibold w-24 text-right">{s.totalTL.toLocaleString("tr-TR", {maximumFractionDigits:0})} ₺</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ReportsModule() {
  const { tr } = useContext(UICtx);
  const [rtab, setRtab]       = useState("groups");
  const [salesData, setSalesData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterYear, setFilterYear]  = useState("");
  const [filterMonth, setFilterMonth] = useState("");
  // General tab data
  const [orders, setOrders]     = useState([]);
  const [payments, setPayments] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [rates, setRates]       = useState({ eurToTl: 38, usdToTl: 34 });

  const fetchSales = useCallback(async (y, m) => {
    setLoading(true);
    try {
      const params = {};
      if (y) params.year = y;
      if (m) params.month = m;
      const { data } = await api.get("/reports/sales", { params });
      setSalesData(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchSales(filterYear || undefined, filterMonth || undefined);
    if (rtab === "general") {
      Promise.all([api.get("/orders"), api.get("/payments"), api.get("/customers"), api.get("/rates")])
        .then(([o, p, c, r]) => { setOrders(o.data); setPayments(p.data); setCustomers(c.data); setRates(r.data); });
    }
  }, [filterYear, filterMonth, rtab, fetchSales]);

  const TABS = [
    { id: "groups",   label: "Ürün Grupları" },
    { id: "period",   label: "Dönemsel Rapor" },
    { id: "general",  label: "Genel & Marj" },
  ];

  // CSV export — ürün grupları
  const handleExportGroups = () => {
    if (!salesData) return;
    const rows = salesData.byGroup.map(g => ({
      "Ürün Grubu": slugLabel(g.groupSlug),
      "Slug": g.groupSlug,
      "Adet": g.count,
      "m²": g.m2,
      "Toplam (₺)": g.totalTL,
    }));
    exportCSV(rows, `ZEN-UrunGrubu-Raporu-${filterYear||"tum"}-${filterMonth||"tum"}.csv`);
  };

  // CSV export — dönemsel
  const handleExportPeriod = () => {
    if (!salesData) return;
    const rows = salesData.byMonth.map(m => ({
      "Yıl": m.year, "Ay": m.month, "Dönem": `${MONTHS_TR[m.month]} ${m.year}`,
      "Sipariş Sayısı": m.orderCount, "Kalem Sayısı": m.itemCount,
      "Toplam (₺)": m.totalTL,
    }));
    exportCSV(rows, `ZEN-Dönemsel-Rapor-${filterYear||"tum"}.csv`);
  };

  // General tab hesaplamalar
  const activeOrders = orders.filter(o => o.status !== "iptal");
  const totalRevenueTL = activeOrders.reduce((s, o) => {
    if (o.currency === "TL") return s + (o.total || 0);
    if (o.currency === "EUR") return s + (o.total || 0) * rates.eurToTl;
    if (o.currency === "USD") return s + (o.total || 0) * rates.usdToTl * 1.02;
    return s;
  }, 0);
  const paidCount = orders.filter(o => o.status === "odendi").length;
  const openCount = orders.filter(o => ["imalatta","kargolanacak","yollandi","onay_bekliyor"].includes(o.status)).length;
  const topCustomers = [...customers].filter(c => c.type !== "Tedarikçi" && c.type !== "Çalışan")
    .sort((a, b) => (b.balance || 0) - (a.balance || 0)).slice(0, 5);

  const maxTL = salesData ? Math.max(...(salesData.byGroup.map(g => g.totalTL)), 1) : 1;

  const years = salesData ? [...new Set(salesData.byMonth.map(m => m.year))].sort((a,b) => b-a) : [];

  return (
    <div className="space-y-4 zen-anim-up">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-extrabold tracking-tight flex items-center gap-2.5">
          <ModuleIcon tab="reports" icon={TrendingUp} />
          {tr("Raporlar")}
        </h2>
        {/* Dönem filtresi */}
        <div className="flex items-center gap-2 flex-wrap">
          <select
            data-testid="report-filter-year"
            value={filterYear}
            onChange={e => { setFilterYear(e.target.value); setFilterMonth(""); }}
            className="zen-input !py-1.5 !px-3 text-xs w-28"
          >
            <option value="">Tüm Yıllar</option>
            {[2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select
            data-testid="report-filter-month"
            value={filterMonth}
            onChange={e => setFilterMonth(e.target.value)}
            className="zen-input !py-1.5 !px-3 text-xs w-32"
            disabled={!filterYear}
          >
            <option value="">Tüm Aylar</option>
            {MONTHS_TR.slice(1).map((m,i) => <option key={i+1} value={i+1}>{m}</option>)}
          </select>
          {(filterYear||filterMonth) && (
            <button onClick={() => { setFilterYear(""); setFilterMonth(""); }} className="text-xs text-slate-400 hover:text-[#E5322A] transition-colors">Temizle ×</button>
          )}
        </div>
      </div>

      {/* Özet kartlar */}
      {salesData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="zen-panel p-4">
            <div className="text-[10px] font-bold uppercase text-slate-500 tracking-wide">Toplam Sipariş</div>
            <div className="text-2xl font-extrabold mt-1 text-[#E5322A]">{salesData.totals.orderCount}</div>
            <div className="text-[10px] text-slate-400 mt-1 leading-tight">Seçili dönemdeki sipariş sayısı (alttaki "adet" satırları kalem sayınızdır)</div>
          </div>
          <div className="zen-panel p-4">
            <div className="text-[10px] font-bold uppercase text-slate-500 tracking-wide">Toplam Ciro</div>
            <div className="text-xl font-extrabold mt-1 zen-mono whitespace-nowrap">{salesData.totals.totalTL.toLocaleString("tr-TR", { maximumFractionDigits: 0 })} ₺</div>
            <div className="text-[10px] text-slate-400 mt-1 leading-tight">Seçili dönemin satış tutarı (KDV hariç)</div>
          </div>
          <div className="zen-panel p-4">
            <div className="text-[10px] font-bold uppercase text-slate-500 tracking-wide">Ürün Grubu</div>
            <div className="text-2xl font-extrabold mt-1">{salesData.byGroup.length}</div>
            <div className="text-[10px] text-slate-400 mt-1 leading-tight">Ciro dağılımındaki ürün grubu sayısı</div>
          </div>
          <div className="zen-panel p-4">
            <div className="text-[10px] font-bold uppercase text-slate-500 tracking-wide">Dönem</div>
            <div className="text-sm font-bold mt-1 text-slate-700 dark:text-slate-300">
              {filterYear ? `${filterYear}${filterMonth ? ` ${MONTHS_TR[+filterMonth]}` : ""}` : "Tümü"}
            </div>
            <div className="text-[10px] text-slate-400 mt-1 leading-tight">Sağ üstteki filtrelerle değiştirilebilir</div>
          </div>
        </div>
      )}

      {/* Alt sekmeler */}
      <div className="flex flex-wrap gap-1 border-b border-slate-200 dark:border-[#22304A]">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setRtab(t.id)} data-testid={`report-tab-${t.id}`}
            className={`px-4 py-2 text-xs font-semibold border-b-2 transition-all -mb-px ${rtab === t.id ? "border-[#E5322A] text-[#E5322A]" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-xs text-slate-400 animate-pulse py-4">Yükleniyor…</div>}

      {/* ─── ÜRÜN GRUPLARI ─── */}
      {rtab === "groups" && !loading && salesData && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">Her gruba tıklayarak detay döküme (kumaş/sistem) bakabilirsiniz.</p>
            <button onClick={handleExportGroups} data-testid="report-export-groups"
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              <Download className="w-3.5 h-3.5" /> CSV İndir
            </button>
          </div>
          <ProductGroupReport data={salesData.byGroup} maxTL={maxTL} />
          {salesData.byGroup.length === 0 && <div className="zen-panel p-6 text-center text-sm text-slate-500">Seçilen dönem için veri bulunamadı.</div>}
        </div>
      )}

      {/* ─── DÖNEMSEL RAPOR ─── */}
      {rtab === "period" && !loading && salesData && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs text-slate-500">Aylık ve yıllık sipariş & ciro dökümü.</p>
            <button onClick={handleExportPeriod} data-testid="report-export-period"
              className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
              <Download className="w-3.5 h-3.5" /> CSV İndir
            </button>
          </div>

          {/* Aylık tablo */}
          <div className="zen-panel overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-100 dark:border-[#22304A]">
              <h3 className="text-sm font-bold">Aylık Döküm</h3>
            </div>
            {salesData.byMonth.length === 0 ? (
              <div className="p-6 text-center text-sm text-slate-500">Veri bulunamadı.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 dark:bg-[#111827]">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-semibold text-slate-500">Dönem</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-slate-500">Sipariş</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-slate-500">Kalem</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-slate-500">Ciro (₺)</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-slate-500">Görsel</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesData.byMonth.map((m, i) => {
                      const maxM = Math.max(...salesData.byMonth.map(x => x.totalTL), 1);
                      const pct = Math.round(m.totalTL / maxM * 100);
                      return (
                        <tr key={i} className="border-t border-slate-100 dark:border-[#22304A] hover:bg-slate-50 dark:hover:bg-[#1a2540] transition-colors">
                          <td className="px-4 py-3 font-semibold">{MONTHS_TR[m.month]} {m.year}</td>
                          <td className="px-4 py-3 text-right zen-mono">{m.orderCount}</td>
                          <td className="px-4 py-3 text-right zen-mono text-slate-500">{m.itemCount}</td>
                          <td className="px-4 py-3 text-right zen-mono font-bold">{m.totalTL.toLocaleString("tr-TR", {maximumFractionDigits:0})}</td>
                          <td className="px-4 py-3">
                            <div className="w-24 h-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                              <div className="h-2 rounded-full bg-[#E5322A]" style={{ width: `${pct}%` }} />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot className="bg-slate-50 dark:bg-[#111827] border-t-2 border-slate-200 dark:border-[#22304A]">
                    <tr>
                      <td className="px-4 py-2.5 font-bold text-xs">TOPLAM</td>
                      <td className="px-4 py-2.5 text-right zen-mono font-bold">{salesData.totals.orderCount}</td>
                      <td className="px-4 py-2.5 text-right zen-mono font-bold text-slate-500">
                        {salesData.byMonth.reduce((s,m) => s+m.itemCount, 0)}
                      </td>
                      <td className="px-4 py-2.5 text-right zen-mono font-bold text-[#E5322A]">
                        {salesData.totals.totalTL.toLocaleString("tr-TR", {maximumFractionDigits:0})}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Yıllık özet */}
          {salesData.byYear.length > 0 && (
            <div className="zen-panel overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-100 dark:border-[#22304A]">
                <h3 className="text-sm font-bold">Yıllık Özet</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 dark:bg-[#111827]">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-semibold text-slate-500">Yıl</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-slate-500">Sipariş</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-slate-500">Kalem</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-slate-500">Ciro (₺)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesData.byYear.map((y, i) => (
                      <tr key={i} className="border-t border-slate-100 dark:border-[#22304A]">
                        <td className="px-4 py-3 font-bold">{y.year}</td>
                        <td className="px-4 py-3 text-right zen-mono">{y.orderCount}</td>
                        <td className="px-4 py-3 text-right zen-mono text-slate-500">{y.itemCount}</td>
                        <td className="px-4 py-3 text-right zen-mono font-bold text-[#E5322A]">{y.totalTL.toLocaleString("tr-TR", {maximumFractionDigits:0})}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── GENEL & MARJ ─── */}
      {rtab === "general" && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="zen-panel p-5"><div className="text-xs font-semibold text-slate-500 uppercase">{tr("Toplam Ciro (TL)")}</div><div className="text-2xl font-extrabold mt-2 text-[#E5322A] zen-mono">{fmt(totalRevenueTL,"TL")}</div></div>
            <div className="zen-panel p-5"><div className="text-xs font-semibold text-slate-500 uppercase">{tr("Ödenmiş Sipariş")}</div><div className="text-2xl font-extrabold mt-2 text-emerald-500">{paidCount}</div></div>
            <div className="zen-panel p-5"><div className="text-xs font-semibold text-slate-500 uppercase">{tr("Açık Sipariş")}</div><div className="text-2xl font-extrabold mt-2 text-amber-500">{openCount}</div></div>
          </div>
          <div className="zen-panel p-5">
            <h3 className="text-sm font-bold mb-3">{tr("Cari Bakiye Durumu")}</h3>
            {topCustomers.length === 0 && <div className="text-xs text-slate-500">{tr("Veri yok")}</div>}
            {topCustomers.map(c => (
              <div key={c.id} className="flex items-center justify-between py-2 border-b last:border-0 border-slate-100 dark:border-[#22304A]">
                <div className="text-sm font-semibold">{c.name}<div className="text-[10px] text-slate-500">{c.type}</div></div>
                <span className={`zen-mono text-sm font-bold ${c.balance > 0 ? "text-rose-500" : "text-emerald-500"}`}>{fmt(c.balance,c.currency)}</span>
              </div>
            ))}
          </div>
          <MarginReport api={api} threshold={20} />
        </div>
      )}
    </div>
  );
}

// ============ KARTELA (ASSET) MANAGEMENT ============

export { ReportsModule, REPORT_GROUP_META };
export default ReportsModule;
